import { DataSource } from '@angular/cdk/table';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { PageEvent } from '@angular/material/paginator';
import {
  ADMIN_ROLE,
  CAREAPP_MANAGER,
  CARER_ROLE,
  COORDINATOR_ROLE,
  CUSTOMER_COORDINATOR_ROLE,
  CUSTOMER_FAMILY_ROLE,
  DASHBOARD_ROLES,
  FAMILY_ROLE,
  STAFF_ROLES,
  TENANT_ADMINISTRATOR,
  TENANT_ROLES,
} from '@careapp-group/core';
import {
  CustomerDto,
  CustomerListDto,
  MemberDto,
  OrganisationDto,
} from '@careapp-group/ng-sdk-v2-new';
import { select, Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { filter, map, take, tap } from 'rxjs/operators';
import * as actions from '../../store/actions/index';
import { auth } from '../../store/auth/auth.selectors';
import { MemberCustomers } from '../../store/member-customers/member-customers.actions';
import { getOwnCustomers } from '../../store/member-customers/member-customers.selectors';
import {
  canAccessAllCustomers,
  getAccessAllCustomers,
  getIsCoordinatorAndDoesNotOverseeAll,
  highestPermissionForCurrentOrganisation,
} from '../../store/organisations/organisations.selectors';
import * as selectors from '../../store/reducers/index';
import { compareAsc, parseISO } from 'date-fns';
import { SORT_LAST_ACTIVITY, SORT_NAME } from './customer-sort-constants';
import { getCustomerPaginators } from '../../store/customers/customers.selectors';
import { UntypedFormControl } from '@angular/forms';
import { DataListTableNavigationOptions } from '../tables/data-list-table/data-list-table.component';
import { NavigationExtras } from '@angular/router';
import { PermissionDto } from '@careapp-group/ng-sdk-v2-new';

@Component({
  selector: 'ca-customer-selection-view',
  templateUrl: './customer-selection-view.component.html',
  styleUrls: ['./customer-selection-view.component.scss'],
})
export class CustomerSelectionViewComponent implements OnInit {
  @Input() adminRoute: boolean = false;
  @Input() showOrganisation: boolean = false;
  @Input() multiple: boolean = true;
  @Input() initialTab: 'all' | 'my' | 'lists';
  @Input() showCreateActions: boolean = false;
  @Input() paginationId: string = 'customerSelectionViewComponent';
  @Input() selectedIds: string[] = [];
  @Input() selectedCustomerListIds: string[] = [];
  // Do not allow selection of customers that do not allow media
  @Input() disableNoMedia: boolean = false;
  // Do not allow selection of customers where the current user has a customer_family role.
  @Input() disableWhereFamilyRole: boolean = false;
  // Only show assigned customers for carers (disable the all search tab)
  @Input() hideUnassigned: boolean;
  // Sorting method
  private _sortMethod = SORT_NAME;
  private disabledCustomers = new Map<string, boolean>();
  public get sortMethod(): string {
    return this._sortMethod;
  }
  public set sortMethod(value: string) {
    this._sortMethod = value;
    this.updateCustomerList();
  }
  // Only show customers where enabled === true
  private _enabledOnly = true;
  public get enabledOnly(): boolean {
    return this._enabledOnly;
  }
  public set enabledOnly(value: boolean) {
    this._enabledOnly = value;
    this.updateCustomerList();
    this.updateEnabledFilter();
  }
  // Link items to their detail page
  @Input() link: boolean;

  @Input() showStars: boolean = false;

  @Output() customerSelected: EventEmitter<CustomerDto> = new EventEmitter();
  @Output() customerListSelected: EventEmitter<CustomerListDto> = new EventEmitter();

  public ALL_CUSTOMERS = 'ALL_CUSTOMERS';
  public MY_CUSTOMERS = 'MY_CUSTOMERS';
  public CUSTOMER_LISTS = 'CUSTOMER_LISTS';

  public ADMIN_ROLE = ADMIN_ROLE;
  public TENANT_ADMINISTRATION_ROLES = [ADMIN_ROLE, ...TENANT_ROLES];
  public BULK_IMPORT_ROLES = [ADMIN_ROLE, CAREAPP_MANAGER];
  public TENANT_ADMINISTRATOR = TENANT_ADMINISTRATOR;
  public MANAGEMENT_ROLES = [...DASHBOARD_ROLES, CAREAPP_MANAGER];
  public CUSTOMER_CREATION_ROLES = [
    ...DASHBOARD_ROLES,
    ADMIN_ROLE,
    CAREAPP_MANAGER,
    ...TENANT_ROLES,
  ];

  public SORT_NAME = SORT_NAME;
  public SORT_LAST_ACTIVITY = SORT_LAST_ACTIVITY;

  public STAFF_AND_TENANT_ROLES_AND_MANAGEMENT_ROLES = [
    ...STAFF_ROLES,
    ...TENANT_ROLES,
    ...this.MANAGEMENT_ROLES,
  ];
  public COORDINATOR = COORDINATOR_ROLE;
  public CUSTOMER_COORDINATOR = CUSTOMER_COORDINATOR_ROLE;
  public CUSTOMER_FAMILY_ROLE = CUSTOMER_FAMILY_ROLE;
  public CARER = CARER_ROLE;
  public FAMILY = FAMILY_ROLE;
  public CAN_VIEW_DISABLED_WARNING = [...STAFF_ROLES, ...DASHBOARD_ROLES, ...TENANT_ROLES];
  public customerLists$: Observable<CustomerListDto[]>;
  public customers: CustomersDataSource;
  public customerListsDataSource: CustomerListsDataSource;
  public myCustomers: CustomersDataSource;
  public allOrganisations$: Observable<{ [id: string]: OrganisationDto[] }>;
  public loading$: Observable<boolean>;
  public page$: Observable<PageEvent>;
  public filter$: Observable<any>;
  public canAccessAllCustomers$: Observable<boolean>;
  public getIsCoordinatorAndDoesNotOverseeAll$: Observable<boolean>;
  // :Note: (TB 28/Sep/'21), Allowing a coordinator to star their customers requires
  // reading if the accessAllCustomers value is explicitly false.
  public accessAllCustomers$: Observable<boolean>;
  public currentMember: MemberDto;
  public highestPermission$: Observable<PermissionDto>;

  // If the current route is "organisations/:id/communities"
  // This will update it to  "organisations/:id/'customer-lists/[id]"
  navigationOptions: DataListTableNavigationOptions = {
    urlFragments: ['customer-lists'],
    extrasBuilder: (activatedRoute) =>
      ({
        relativeTo: activatedRoute.parent.parent,
      } as NavigationExtras),
  };

  selectedTab = new UntypedFormControl(0);

  _search = '';
  set search(value: string) {
    this._search = value;
    this.updateSearch(this._search);
  }
  get search() {
    return this._search;
  }

  public tableConfig = [
    {
      key: 'name',
      sortable: false,
      title: 'Customer',
    },
  ];

  public customerListTableConfig = [
    {
      key: 'name',
      sortable: false,
      title: 'CustomerList',
    },
  ];

  constructor(private store: Store<any>) {}

  updateCustomerSort(event: string) {
    if (event != null) {
      this.sortMethod = event;
    }
  }

  updateCustomerEnabled(event: boolean) {
    if (event != null) {
      this.enabledOnly = event;
    }
  }

  ngOnInit() {
    this.loading$ = this.store.select(selectors.getLoadingAll(selectors.CUSTOMERS));
    this.highestPermission$ = this.store.select(highestPermissionForCurrentOrganisation);
    this.store.dispatch(actions.Customers.ClearPaginationAction(this.paginationId));
    this.store.dispatch(
      actions.Customers.UpdateFilterByIdAction({
        order: `lastName ASC`,
        paginatorId: this.paginationId,
        enabledOnly: this.enabledOnly,
      })
    );
    this.store.dispatch(
      actions.Customers.RequestAllPaginated({
        paginatorId: this.paginationId,
        clear: true,
      })
    );

    this.store.dispatch(actions.CustomerLists.RequestAllAction());

    this.store
      .select(auth.getLoggedInUser)
      .pipe(take(1))
      .subscribe((user) => {
        this.store.dispatch(MemberCustomers.RequestAllPaginatedAction({}, user.id, true));
        this.currentMember = user;
      });

    this.updateCustomerLists();
    this.updateCustomerList();
    this.allOrganisations$ = this.store.select(selectors.getEntities(selectors.ORGANISATIONS));
    this.myCustomers = new CustomersDataSource(
      this.store.select(getOwnCustomers).pipe(
        tap((customers) => {
          customers.forEach((customer) => this.setCustomerDisabledStatus(customer));
        })
      )
    );
    this.page$ = this.store.select(getCustomerPaginators).pipe(
      filter((data) => !!data && data[this.paginationId] != null),
      map((data) => data[this.paginationId].page)
    );
    this.filter$ = this.store.select(getCustomerPaginators).pipe(
      filter((data) => !!data && data[this.paginationId] != null),
      map((data) => data[this.paginationId].filter)
    );
    this.canAccessAllCustomers$ = this.store.select(canAccessAllCustomers);
    this.accessAllCustomers$ = this.store.select(getAccessAllCustomers);
    this.getIsCoordinatorAndDoesNotOverseeAll$ = this.store.select(
      getIsCoordinatorAndDoesNotOverseeAll
    );

    this.highestPermission$
      .pipe(
        filter((value) => !!value),
        take(1)
      )
      .subscribe((permission) => this.selectInitialTabFromPermission(permission));
  }

  setCustomerDisabledStatus(customer: CustomerDto) {
    if (!customer) {
      return;
    }

    const isDisabled =
      (this.disableNoMedia && customer.settings?.allowMemoImages === false) ||
      (this.disableWhereFamilyRole &&
        (customer.permissions ?? []).some((p) => p.role === CUSTOMER_FAMILY_ROLE));

    this.disabledCustomers.set(customer.id, isDisabled);
  }

  // :Note: If you're changing tab order, please be sure to update the
  // index of each tab for an initial tab to be set correctly.
  selectInitialTabFromPermission(permission: PermissionDto) {
    const role = permission.role;
    const accessAllCustomers = permission?.qualifications?.accessAllCustomers ?? false;
    if (this.initialTab != null) {
      // Family: No tabs.
      // Limited Carer: My, Search all, Lists
      if (role == CARER_ROLE && accessAllCustomers != true) {
        switch (this.initialTab) {
          case 'my':
            this.selectedTab.setValue(0);
            break;
          case 'all':
            this.selectedTab.setValue(1);
            break;
          case 'lists':
            this.selectedTab.setValue(2);
            break;
        }
        // Limited Coordinator: All, my, lists
      } else if (role == COORDINATOR_ROLE && accessAllCustomers != true) {
        switch (this.initialTab) {
          case 'all':
            this.selectedTab.setValue(0);
            break;
          case 'my':
            this.selectedTab.setValue(1);
            break;
          case 'lists':
            this.selectedTab.setValue(2);
            break;
        }
      } else if ([...DASHBOARD_ROLES, CARER_ROLE].includes(role)) {
        // Oversee all Carer: All, Lists
        // Non limited coordinator dashboard users: All, lists
        switch (this.initialTab) {
          case 'all':
            this.selectedTab.setValue(0);
            break;
          case 'lists':
            this.selectedTab.setValue(1);
            break;
        }
      }
    }
  }

  updateCustomerLists() {
    this.customerLists$ = this.store.pipe(select(selectors.getAll(selectors.CUSTOMER_LISTS)));
    this.customerListsDataSource = new CustomerListsDataSource(this.customerLists$);
  }

  updateCustomerList() {
    this.customers = new CustomersDataSource(
      this.store.pipe(
        select(selectors.getAllPaginated(selectors.CUSTOMERS, this.paginationId)),
        map((customers: CustomerDto[]) => {
          customers.forEach((customer) => this.setCustomerDisabledStatus(customer));

          return (
            this.enabledOnly ? customers.filter((customer) => customer.enabled != false) : customers
          ).sort((a, b) => this.sortCustomers(a, b));
        })
      )
    );
  }

  updateEnabledFilter() {
    this.store.dispatch(
      actions.Customers.UpdateFilterByIdAction({
        paginatorId: this.paginationId,
        enabledOnly: this.enabledOnly,
      })
    );
  }

  sortCustomers(a: CustomerDto, b: CustomerDto): number {
    switch (this.sortMethod ?? SORT_NAME) {
      case this.SORT_LAST_ACTIVITY:
        const aLastContent = a.lastContentAt != null ? parseISO(a.lastContentAt) : new Date(0);
        const bLastContent = b.lastContentAt != null ? parseISO(b.lastContentAt) : new Date(0);
        return compareAsc(aLastContent, bLastContent);
      // By default, sort by name.
      default:
        const aName = a.lastName ?? '' + a.firstName ?? '';
        const bName = b.lastName ?? '' + b.firstName ?? '';
        return aName.toLowerCase().localeCompare(bName.toLowerCase());
    }
  }

  paginate(page: PageEvent) {
    this.store.dispatch(
      actions.Customers.PageChangeByIdAction({ page, paginatorId: this.paginationId })
    );
  }

  updateSearch(search: string) {
    if (search.length > 0) {
      this.store.dispatch(
        actions.Customers.UpdateFilterByIdAction({
          name: search,
          paginatorId: this.paginationId,
          enabledOnly: this.enabledOnly,
        })
      );
    } else {
      this.store.dispatch(
        actions.Customers.UpdateFilterByIdAction({
          paginatorId: this.paginationId,
          enabledOnly: this.enabledOnly,
        })
      );
    }
  }

  sort(event) {
    if (event.direction) {
      this.store.dispatch(
        actions.Customers.UpdateFilterByIdAction({
          paginatorId: this.paginationId,
          order: `${event.active} ${event.direction.toUpperCase()}`,
          enabledOnly: this.enabledOnly,
        })
      );
    }
  }

  // Only to be used by coordinators with false accessAllCustomers
  addCustomerCoordinator(event: Event, customer: CustomerDto) {
    event.preventDefault();
    event.stopPropagation();
    if (this.currentMember) {
      this.store.dispatch(
        actions.CustomerTeam.RequestCreateForCustomerAction({
          memberId: this.currentMember.id,
          role: CUSTOMER_COORDINATOR_ROLE,
          customerId: customer.id,
        })
      );
    }
  }

  // Only to be used by coordinators with false accessAllCustomers
  removeCustomerCoordinator(event: Event, customer: CustomerDto) {
    event.preventDefault();
    event.stopPropagation();
    this.store.dispatch(
      actions.CustomerTeam.RequestRemoveFromCustomerAction({
        memberId: this.currentMember.id,
        customerId: customer.id,
      })
    );
  }

  // For passing into the filter pipe, to filter a main list of customers by
  // who can and cannot have media posted.
  customerAllowsMediaFilter(allowMemoImages = true) {
    // If allowMemoImages is true, any settings with true or null will pass.
    // If allowMemoImages is false, only settings explicitly false will pass.
    return (item: CustomerDto) =>
      (item && item.settings?.allowMemoImages == allowMemoImages) ?? allowMemoImages;
  }

  noop() {
    // because void in template gives an error
  }
}

export class CustomersDataSource extends DataSource<CustomerDto | CustomerDto> {
  constructor(public data: Observable<CustomerDto[] | CustomerDto[]>) {
    super();
  }

  connect() {
    return this.data;
  }

  disconnect() {
    // lint
  }
}
export class CustomerListsDataSource extends DataSource<CustomerListDto> {
  constructor(public data: Observable<CustomerListDto[]>) {
    super();
  }

  connect() {
    return this.data;
  }

  disconnect() {
    // lint
  }
}
